import type { UseFieldArrayReturn, UseFormReturn } from 'react-hook-form';
import { ConfigurationField, ConfigurationSelect } from './ConfigurationField';
import type { PricingConfigurationFormValues } from './pricingConfigurationSchema';

interface Props {
  form: UseFormReturn<PricingConfigurationFormValues>;
  windows: UseFieldArrayReturn<PricingConfigurationFormValues, 'publicationWindows'>;
  horizonDays: number;
  errorFor: (path: string) => string | undefined;
}

const INTERVALS = [1, 3, 6, 12, 24];

/**
 * Abschnitt 4: Takt, erster Lauf und Horizont (aus der Version). Die Übertragungsfenster gelten
 * beim Versand an Channex: außerhalb wartet eine Übertragung bis zum nächsten Fenster.
 */
export function ScheduleSection({ form, windows, horizonDays, errorFor }: Props) {
  return (
    <>
      <div className="grid gap-2.5 sm:grid-cols-3">
        <ConfigurationSelect
          label="Berechnungstakt"
          error={errorFor('calculationIntervalHours')}
          {...form.register('calculationIntervalHours')}
        >
          {INTERVALS.map((hours) => (
            <option key={hours} value={hours}>
              {hours === 1 ? 'Jede Stunde' : `Alle ${hours} Stunden`}
            </option>
          ))}
        </ConfigurationSelect>
        <ConfigurationField
          label="Erster Lauf (Ortszeit)"
          type="time"
          error={errorFor('calculationStartLocalTime')}
          {...form.register('calculationStartLocalTime')}
        />
        <div className="bg-paper rounded-control flex min-h-[60px] flex-col justify-center px-3.5 py-2">
          <span className="text-ink-soft font-mono text-[10px] uppercase tracking-[0.14em]">
            Planungshorizont
          </span>
          <b className="text-base">{horizonDays} Tage</b>
        </div>
      </div>
      <span className="flex flex-col gap-0.5 pt-1">
        <span className="text-sm font-bold">Übertragungsfenster</span>
        <span className="text-ink-soft text-xs">
          Automatisch berechnete Preise und Mindestnächte gehen nur innerhalb dieser Zeiten
          (Ortszeit des Objekts) an Channex. Was außerhalb berechnet wird, wartet bis zum nächsten
          Fenster und steht im Verlauf als „Eingereiht“; ein Tag, der beim Warten vorbeigeht,
          entfällt.
        </span>
      </span>
      {windows.fields.map((window, index) => (
        <div key={window.id} className="flex flex-wrap items-end gap-2.5">
          <ConfigurationField
            label={`Von (Fenster ${index + 1})`}
            type="time"
            className="w-40"
            error={errorFor(`publicationWindows.${index}.startLocalTime`)}
            {...form.register(`publicationWindows.${index}.startLocalTime`)}
          />
          <ConfigurationField
            label={`Bis (Fenster ${index + 1})`}
            type="time"
            className="w-40"
            error={errorFor(`publicationWindows.${index}.endLocalTime`)}
            {...form.register(`publicationWindows.${index}.endLocalTime`)}
          />
          {windows.fields.length > 1 && (
            <button
              type="button"
              onClick={() => windows.remove(index)}
              className="text-sealwax rounded-pill min-h-11 px-2.5 text-xs font-bold"
            >
              Fenster {index + 1} entfernen
            </button>
          )}
        </div>
      ))}
      {errorFor('publicationWindows') && (
        <p className="text-sealwax text-xs">{errorFor('publicationWindows')}</p>
      )}
      <button
        type="button"
        onClick={() => windows.append({ startLocalTime: '06:00', endLocalTime: '23:00' })}
        className="border-forest text-forest rounded-pill min-h-11 self-start border-[1.5px] border-dashed px-3 text-xs font-bold"
      >
        + Fenster hinzufügen
      </button>
    </>
  );
}
